import { CurrencyPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrderService } from 'src/app/services/order.service';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { CreateInvoiceRequestModel } from 'src/app/common/CreateInvoiceRequestModel';
import { InvoiceService } from 'src/app/services/invoice.service';
import { Router } from '@angular/router';
import { AppProfileService } from 'src/app/services/profile.service';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { InvoiceFilter } from 'src/app/common/InvoiceFilter';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { PagedList } from 'src/app/common/PagedList';
import { Order } from 'src/app/common/Order';
import { InvoiceOrderSearchComponent } from '../invoice-order-search/invoice-order-search.component';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { map, Observable, tap } from 'rxjs';
import { GridPdfColumnsDirective } from '../grid/grid-pdf-columns.directive';
import { PdfPageComponent } from '../grid/pdf-page.component';
import { GridNoRecordsComponent } from '../grid/grid-no-records.component';

@Component({
    imports: [CurrencyPipe, KENDO_GRID, InvoiceOrderSearchComponent, AsyncClickDirective, GridPdfColumnsDirective, PdfPageComponent, GridNoRecordsComponent],
    selector: 'app-invoice-order-list',
    templateUrl: './invoice-order-list.component.html',
    styleUrls: ['./invoice-order-list.component.css']
})
export class InvoiceOrderListComponent implements OnInit {
  private profileService = inject(AppProfileService);
  orderService = inject(OrderService);
  private invoiceService = inject(InvoiceService);
  private router = inject(Router);
  private callApiService = inject(CallApiService);

  private destroyRef = inject(DestroyRef);

  // The search component emits its criteria on init and again on every profile emission and
  // every date/customer change; each emission is a new search, and the newest one wins.
  filter: InvoiceFilter = new InvoiceFilter(null, null, null);
  faFilePdf = faFilePdf;
  selectedOrders: string[] = [];
  public profile: ExtendedProfile = null;

  // #648: server-side paging/sorting; default order = newest first. Selection persists across
  // pages via [kendoGridSelectBy]="'id'" + selectedOrders keys.
  public list = new PagedList<Order>({
    sortFields: { orderDateTime: 'orderDateTime', orderNumber: 'orderNumber' },
    defaultSort: [{ field: 'orderDateTime', dir: 'desc' }],
    fetch: (paging) =>
      this.filter
        ? this.orderService
            .getBillableOrders(this.filter.customerNameOrNumber, this.filter.dateFrom, this.filter.dateTo, paging)
            .pipe(
              map((r) => {
                r.items.forEach((o) => (o.orderDateTime = new Date(o.orderDateTime)));
                return r;
              })
            )
        : null,
  });

  search(filter: InvoiceFilter) {
    this.filter = filter;
    this.list.search();
  }

  ngOnInit() {
    this.filter = this.invoiceService.getDefaultFilter();
    this.profileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.profile = p;
    });
  }

  public selectionChange(rows: string[]) {
    this.selectedOrders = rows;
  }

  public onCreateInvoices(): Observable<unknown> {
    const request = new CreateInvoiceRequestModel();
    request.orderIds = this.selectedOrders;
    request.filterFromDateTime = this.filter.dateFrom;
    request.filterToDateTime = this.filter.dateTo;

    return this.callApiService
      .callWithToasts$(
        this.invoiceService.createInvoices(request),
        $localize`Rechnungen werden erstellt.`,
        $localize`Beim Erstellen der Rechnungen ist ein Fehler aufgetreten.`
      )
      .pipe(tap(() => this.router.navigateByUrl('shop-invoice-list')));
  }
}
